import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, DollarSign, FileText, CalendarDays, Car,
  Wrench, Users
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";

const CHART_COLORS = ["#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#84CC16"];
const PIE_COLORS = ["#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444", "#06B6D4"];

const formatINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const ReportsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reports/dashboard`);
      setData(r.data);
    } catch { toast.error("Failed to load reports"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-60px)]"><p className="text-[13px] text-[#94A3B8]">Loading reports...</p></div>;
  if (!data) return <div className="p-6 text-center text-[#94A3B8]">No data available</div>;

  const m = data.metrics || {};

  return (
    <div className="p-5 space-y-5 overflow-y-auto h-[calc(100vh-60px)]" data-testid="reports-dashboard">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI icon={FileText} iconBg="bg-[#EFF6FF]" iconColor="text-[#3B82F6]" label="Job Cards" value={m.total_job_cards} />
        <KPI icon={CalendarDays} iconBg="bg-[#FEF3C7]" iconColor="text-[#D97706]" label="Bookings" value={m.total_bookings} />
        <KPI icon={Car} iconBg="bg-[#F0FDF4]" iconColor="text-[#16A34A]" label="Vehicles" value={m.total_vehicles} />
        <KPI icon={DollarSign} iconBg="bg-[#F0FDF4]" iconColor="text-[#16A34A]" label="Revenue" value={formatINR(m.total_revenue)} />
        <KPI icon={TrendingUp} iconBg="bg-[#FEF3C7]" iconColor="text-[#D97706]" label="Pending" value={formatINR(m.pending_revenue)} />
        <KPI icon={BarChart3} iconBg="bg-[#EDE9FE]" iconColor="text-[#7C3AED]" label="Invoices" value={m.total_invoices} />
      </div>

      {/* Row 1 — Bookings per week + Revenue per month */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Bookings per Week" subtitle="Last 8 weeks" icon={CalendarDays}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.bookings_by_week} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} />
              <Bar dataKey="bookings" fill="#3B82F6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue per Month" subtitle="Paid invoices – last 6 months" icon={DollarSign}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.revenue_by_month} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} tickFormatter={v => v >= 1000 ? `₹${(v/1000).toFixed(0)}k` : `₹${v}`} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }} formatter={v => [formatINR(v), "Revenue"]} />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4, fill: "#10B981" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2 — Job Status pie + Service breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Job Card Status" subtitle="Current distribution" icon={FileText}>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="55%" height={220}>
              <PieChart>
                <Pie data={data.job_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={85} innerRadius={45}
                  paddingAngle={3} stroke="none">
                  {data.job_status.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {data.job_status.map((item, idx) => (
                <div key={item.status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                    <span className="text-[12px] text-[#475569]">{item.status}</span>
                  </div>
                  <span className="text-[13px] font-bold text-[#1E293B]">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Service Type Breakdown" subtitle="All job cards by service" icon={Wrench}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.service_breakdown} layout="vertical" margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
              <YAxis type="category" dataKey="service" tick={{ fontSize: 11, fill: "#475569" }} width={120} />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="count" fill="#8B5CF6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 3 — Tech workload + Vehicles by make + Booking status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Workload" subtitle="Active jobs per person" icon={Users}>
          {data.tech_workload.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.tech_workload} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="active_jobs" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No active jobs" />}
        </ChartCard>

        <ChartCard title="Vehicles by Make" subtitle="Top manufacturers" icon={Car}>
          {data.vehicles_by_make.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.vehicles_by_make} dataKey="count" nameKey="make" cx="50%" cy="50%" outerRadius={75} stroke="none">
                  {data.vehicles_by_make.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No vehicles registered" />}
        </ChartCard>

        <ChartCard title="Booking Status" subtitle="Current distribution" icon={CalendarDays}>
          {data.booking_status.length > 0 ? (
            <div className="space-y-3 py-2">
              {data.booking_status.map((item, idx) => {
                const total = data.booking_status.reduce((s, b) => s + b.count, 0);
                const pct = total > 0 ? (item.count / total * 100).toFixed(0) : 0;
                return (
                  <div key={item.status}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[12px] text-[#475569]">{item.status}</span>
                      <span className="text-[12px] font-bold text-[#1E293B]">{item.count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <EmptyState text="No bookings yet" />}
        </ChartCard>
      </div>
    </div>
  );
};

const KPI = ({ icon: Icon, iconBg, iconColor, label, value }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F1F5F9]">
    <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center mb-2`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{label}</p>
    <p className="text-[22px] font-bold text-[#1E293B] mt-0.5">{value}</p>
  </div>
);

const ChartCard = ({ title, subtitle, icon: Icon, children }) => (
  <div className="bg-white rounded-2xl border border-[#F1F5F9] shadow-sm" data-testid={`chart-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="px-5 py-3 border-b border-[#F1F5F9] flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#64748B]" />
      <div>
        <p className="text-[14px] font-bold text-[#1E293B]">{title}</p>
        {subtitle && <p className="text-[10px] text-[#94A3B8]">{subtitle}</p>}
      </div>
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="flex items-center justify-center h-[180px]">
    <p className="text-[12px] text-[#CBD5E1]">{text}</p>
  </div>
);

export default ReportsPage;
